// Featurebase, booted once, on the console and on the marketing site.
//
// THE SITE IS THE PART THAT NEEDS CARE. One Inertia root serves both surfaces
// -- see the comment in entrypoints/inertia.tsx -- and layouts/website.html.erb
// runs OUR OWN banner on yupkit.com, because a consent product whose website
// does not run it is making an argument it will not take. So the visitor chat
// there is a third-party runtime on a page our own notice governs.
//
// It is held rather than excluded. The SDK injects its script at runtime, which
// is precisely what the gate catches: `data-block` on our loader tag arms
// holding for every third-party insert (loader.ts#armGate), so the Featurebase
// script waits with everything else until the visitor has answered. The
// attribute is on the website layout for exactly this.
//
// WHICH MEANS THE NOTICE HAS TO NAME FEATUREBASE, and not as paperwork: the
// gate releases a host only when the decision licenses it, and a notice that
// discloses nobody licenses nothing. Until Featurebase is a DeclaredVendor on
// the yupkit.com tenant and a version carrying it is published, the chat simply
// never appears on the site -- held, silent, and safe. See SQ5-209.
//
// The console is ours, behind a sign-in, and no notice governs it, so the
// messenger runs there unconditionally. The sign-in screen is the same surface
// and gets the same anonymous visitor chat.
import type { Page } from '@inertiajs/core'
import { FeaturebaseProvider } from 'featurebase-js/react'
import type { ReactElement } from 'react'

import type { SharedProps } from '@/types'

/**
 * The workspace the console reports into. Public in the same sense the banner's
 * `data-key` is: it identifies a workspace and authorises nothing, and it ships
 * in the bundle either way.
 */
const APP_ID = '6aa3bf45feefcf50a059ea71'

/**
 * Inertia's own wrap-the-app hook, which is what keeps this to ONE boot for the
 * lifetime of the tab: it runs against the initial page, not on every visit, so
 * the messenger is not torn down and re-created on each navigation.
 *
 * Returns the app untouched on the server. The Provider boots a browser SDK and
 * there is no browser there.
 *
 * THE TOKEN IS READ ONCE, FROM THE INITIAL PAGE, and that is correct rather
 * than a shortcut: signing in and signing out are both native form posts, so
 * either one replaces the document and this runs again with the new answer.
 * Nothing an Inertia visit can do changes who is signed in.
 */
/**
 * NOT EVERY PAGE THIS RUNS FOR HAS AN `auth` PROP. The tenant console does; the
 * sign-in screens and the cross-tenant operator console do not, and neither
 * identifies anybody. Narrowed rather than asserted, because asserting would
 * be a type that lies on two thirds of the surfaces this boots on.
 */
function tokenOn(page: Page): string | null | undefined {
  return (page.props as Partial<SharedProps>).auth?.featurebase_jwt
}

export function withFeaturebase(
  app: ReactElement,
  { ssr, page }: { ssr: boolean; page: Page },
): ReactElement {
  if (ssr) return app

  return (
    <FeaturebaseProvider
      appId={APP_ID}
      // Absent for a signed-out visitor and wherever no secret is configured,
      // which is every environment but production. The widget is anonymous
      // then, which is Phase 1's behaviour and not a failure.
      featurebaseJwt={tokenOn(page) ?? undefined}
    >
      {app}
    </FeaturebaseProvider>
  )
}
